import { useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/dexie.js';
import { pedirFoto } from '../sync/fotos.js';
import { Ico, type NombreIcono } from './Iconos.js';
import { pictograma } from './pictogramas.js';

export type Tamano = 'sm' | 'md' | 'lg' | 'xl';

const CAJA: Record<Tamano, string> = {
  sm: 'size-9 rounded-lg',
  md: 'size-11 rounded-xl',
  lg: 'size-14 rounded-2xl',
  xl: 'size-28 rounded-3xl',
};

const GLIFO: Record<Tamano, string> = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
  xl: 'size-12',
};

/**
 * Object URLs, one per photo, for the life of the page.
 *
 * A photo's name is its content, so the URL made for it the first time is right forever — and
 * keeping it means a thumbnail that scrolls back into view, or a screen opened a second time,
 * draws on the first frame instead of flashing its placeholder while IndexedDB answers again.
 * Bounded by the number of photos the shop has, and each one is a handle on bytes the browser
 * already holds on disk.
 */
const urls = new Map<string, string>();

/**
 * The photo's URL; `null` when there is none to draw; `undefined` while IndexedDB is still
 * answering, so the first frame can hold the space instead of flashing a pictogram that the
 * photo then replaces.
 *
 * A photo this device does not have is asked for from the server, unless `pedir` is false.
 */
export function useFoto(huella: string | null | undefined, pedir = true): string | null | undefined {
  const leida = useLiveQuery(
    async () => ({ huella, fila: !huella || urls.has(huella) ? null : ((await db.fotos.get(huella)) ?? null) }),
    [huella],
    undefined,
  );
  /**
   * The answer about this photo, and no other. Asked about a new one, `useLiveQuery` keeps the last
   * answer until the new one comes: a product given another photo had the bytes of the one before
   * drawn, and kept in `urls` under the new name, for as long as the page lived (2026-10-20).
   */
  const fila = leida !== undefined && leida.huella === huella ? leida.fila : undefined;

  const falta = Boolean(huella) && fila === null && !urls.has(huella!);
  useEffect(() => {
    if (pedir && falta && huella) pedirFoto(huella);
  }, [pedir, falta, huella]);

  if (!huella) return null;
  const hecha = urls.get(huella);
  if (hecha) return hecha;
  if (fila === undefined) return undefined;
  if (fila === null) return null;
  const url = URL.createObjectURL(fila.datos);
  urls.set(huella, url);
  return url;
}

/** What a thumbnail needs to know about a product: its photo, and enough to draw its kind. */
export interface ProductoMiniatura {
  nombre: string;
  nombreEn?: string | null;
  categoria: string;
  foto?: string | null;
}

/**
 * A product's picture: its photo when this device has it, the drawing of its kind when it does
 * not — never an empty box, and never a broken image.
 *
 * Decorative to a screen reader (`alt=""`): it always sits beside the product's name, and
 * reading "Arroz 80/20, image, Arroz 80/20" down a list of forty helps nobody.
 */
export function Miniatura({
  producto,
  tamano = 'md',
  className = '',
  soloLocal = false,
}: {
  producto: ProductoMiniatura;
  tamano?: Tamano;
  /**
   * For a row that has to give the picture up on the narrowest phones. In a dense row the name
   * is the thing that must fit; the picture only helps while there is room for both.
   */
  className?: string;
  /**
   * Draws only what this device already holds and never asks the server: for the customer's
   * screen, a window that is not the app and runs no sync. The till beside it asks for the same
   * photos, and they show up here when they land.
   */
  soloLocal?: boolean;
}) {
  const url = useFoto(producto.foto, !soloLocal);
  const caja = `${CAJA[tamano]} ${className}`;

  if (url) {
    return (
      <span className={`miniatura ${caja}`}>
        {/* Fades in only when it arrives late: a cached one is there on the first frame. */}
        <img src={url} alt="" decoding="async" className="aparece" draggable={false} />
      </span>
    );
  }

  if (url === undefined) return <span className={`miniatura ${caja}`} aria-hidden />;

  const { glifo: Glifo, tinte } = pictograma(producto);
  return (
    <span className={`chip chip-${tinte} ${caja}`} aria-hidden>
      <Glifo className={GLIFO[tamano]} strokeWidth={2} />
    </span>
  );
}

/**
 * The same square, for a line with no product behind it — a venta rápida — so a cart with one
 * in it still lines up.
 */
export function MiniaturaIcono({
  icono,
  tamano = 'md',
  className = '',
}: {
  icono: NombreIcono;
  tamano?: Tamano;
  /** As on `Miniatura`, so it goes wherever the photos beside it go. */
  className?: string;
}) {
  return (
    <span className={`chip chip-neutro ${CAJA[tamano]} ${className}`} aria-hidden>
      <Ico icono={icono} className={GLIFO[tamano]} />
    </span>
  );
}

